import { useEffect, useState } from "react";
import styles from "../fields/FloatingInput.module.css";
import FieldHint from "../fields/FieldHint";
import { listMagasins, magasinsForRegion, type RegionGroup } from "@/lib/listMagasins";

interface StepMagasinProps {
  region: string;
  magasinId: string;
  onSelect: (magasinId: string, label: string, region: string) => void;
  onRegionChange: (region: string) => void;
  errors: Record<string, string>;
}

export default function StepMagasin({
  region,
  magasinId,
  onSelect,
  onRegionChange,
  errors,
}: StepMagasinProps) {
  const [groups, setGroups] = useState<RegionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    listMagasins(controller.signal)
      .then((res) => {
        if (res.status === "aborted") return;
        if (res.status === "ok") {
          setGroups(res.groups);
          setEmpty(res.groups.length === 0);
          setFailed(false);
        } else {
          setFailed(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const magasins = magasinsForRegion(groups, region);

  return (
    <fieldset style={{ border: "none" }}>
      <legend
        style={{ fontSize: 16, fontWeight: 500, marginBottom: 12, display: "block", width: "100%" }}
      >
        Quelle est la localisation de votre projet ?
      </legend>

      <FieldHint>
        Choisissez le magasin le plus proche de votre chantier. Si vos travaux concernent une
        résidence secondaire, sélectionnez le magasin le plus proche de celle-ci : c'est lui qui
        vous accompagnera pour le suivi de votre projet.
      </FieldHint>

      {/* Région */}
      <div className={styles.field}>
        <label className={styles.label} htmlFor="region">Région *</label>
        <select
          id="region"
          name="region"
          className={`${styles.input} ${errors.region ? styles.hasError : ""}`}
          value={region}
          disabled={loading || failed || empty}
          onChange={(e) => onRegionChange(e.target.value)}
        >
          <option value="">{loading ? "Chargement…" : "— Choisir une région —"}</option>
          {groups.map((g) => (
            <option key={g.region} value={g.region}>{g.region}</option>
          ))}
        </select>
        {errors.region && <span className={styles.errorLabel}>{errors.region}</span>}
      </div>

      {/* Magasin */}
      <div className={styles.field} style={{ marginTop: 16 }}>
        <label className={styles.label} htmlFor="magasin">Magasin *</label>
        <select
          id="magasin"
          name="magasin"
          className={`${styles.input} ${errors.magasin ? styles.hasError : ""}`}
          value={magasinId}
          disabled={!region || magasins.length === 0}
          onChange={(e) => {
            const m = magasins.find((x) => x.id === e.target.value);
            if (m) onSelect(m.id, m.label, region);
            else onSelect("", "", region);
          }}
        >
          <option value="">— Choisir un magasin —</option>
          {magasins.map((m) => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </select>
        {errors.magasin && <span className={styles.errorLabel}>{errors.magasin}</span>}
      </div>

      {failed && (
        <span className={styles.errorLabel} style={{ marginTop: 8, display: "inline-block" }}>
          Impossible de charger la liste des magasins. Réessayez plus tard.
        </span>
      )}
      {empty && !failed && (
        <span style={{ marginTop: 8, display: "inline-block" }}>
          Aucun magasin disponible pour le moment.
        </span>
      )}
    </fieldset>
  );
}
